import { Component, OnInit } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TemplateService } from 'src/app/shared/services/template.service';
import { CompanyNewOrderDialogService } from 'src/app/company/company-new-order-dialog/company-new-order-dialog.service';

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
  providers: [ConfirmationService, MessageService],
})
export class MainLayoutComponent implements OnInit {
  collapsed: boolean = false;
  companyNotifs: any[] = [];
  newNotifs: any[] = [];
  cancellationWarnings: any[] = [];
  newCancellationWarnings: any[] = [];

  private orderAudio = new Audio();
  private notifAudio = new Audio();
  alertAudioOn: boolean = false;

  private readonly NEW_ORDER_SOUND = 'assets/sounds/new-order-alert.mpeg';
  private readonly NOTIF_SOUND = 'assets/sounds/alert.mpeg';
  private readonly NEW_ORDER_TYPES = new Set([
    'newrequest',
    'neworderassign',
    'neworder',
  ]);
  /** On-road: provider is already assigned — company gets info toast only, no accept modal. */
  private readonly ON_ROAD_INFO_TYPES = new Set([
    'onroadproviderassigned',
    'onroadorderassigned',
  ]);
  private openedNewOrderNotifIds = new Set<string>();

  constructor(
    public templateService: TemplateService,
    private afAuth: AngularFireAuth,
    private afs: AngularFirestore,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private newOrderDialog: CompanyNewOrderDialogService
  ) {
    this.orderAudio.src = this.NEW_ORDER_SOUND;
    this.orderAudio.load();
    this.notifAudio.src = this.NOTIF_SOUND;
    this.notifAudio.load();
  }

  ngOnInit() {
    this.templateService.getMessage().subscribe((res) => {
      this.collapsed = this.templateService.collapsed;
    });

    this.getCompanyNotifications();
    this.getCancellationWarnings();
  }

  private normalizeNotifType(notif: any): string {
    return String(notif?.type || '').toLowerCase().replace(/[\s_-]/g, '');
  }

  private isOnRoadInfoNotification(notif: any): boolean {
    const type = this.normalizeNotifType(notif);
    if (this.ON_ROAD_INFO_TYPES.has(type)) {
      return true;
    }
    const orderType = String(notif?.orderType || notif?.serviceType || '')
      .toLowerCase()
      .trim();
    return orderType === 'on-road' || orderType === 'onroad';
  }

  private isNewOrderNotification(notif: any): boolean {
    if (this.isOnRoadInfoNotification(notif)) {
      return false;
    }
    return this.NEW_ORDER_TYPES.has(this.normalizeNotifType(notif));
  }

  playNewOrderAlert() {
    try {
      this.notifAudio.pause();
      this.notifAudio.currentTime = 0;
      this.orderAudio.currentTime = 0;
      void this.orderAudio.play();
      this.alertAudioOn = true;
    } catch (error) {
      console.warn('New order alert sound failed', error);
    }
  }

  playNotificationAlert() {
    try {
      this.orderAudio.pause();
      this.orderAudio.currentTime = 0;
      this.notifAudio.currentTime = 0;
      void this.notifAudio.play();
      this.alertAudioOn = true;
    } catch (error) {
      console.warn('Notification alert sound failed', error);
    }
  }

  playAlert() {
    this.playNewOrderAlert();
  }

  playCancellationAlert() {
    this.playNotificationAlert();
  }

  stopAlert() {
    this.orderAudio.pause();
    this.orderAudio.currentTime = 0;
    this.notifAudio.pause();
    this.notifAudio.currentTime = 0;
    this.alertAudioOn = false;
  }

  getCompanyNotifications() {
    this.newNotifs = [];
    this.afAuth.authState.subscribe((user) => {
      if (user) {
        this.afs
          .collection('company-notifications', (ref) =>
            ref.where('receiverId', '==', user.uid).where('seen', '==', false)
          )
          .valueChanges({ idField: 'id' })
          .subscribe((res) => {
            console.log(res);

            this.companyNotifs = res;

            this.newNotifs = this.companyNotifs.filter(
              (el) => !el.shown && el?.id && !this.openedNewOrderNotifIds.has(el.id)
            );
            if (this.newNotifs.length !== 0) {
              const newOrderNotifs = this.newNotifs.filter((el) =>
                this.isNewOrderNotification(el)
              );
              const onRoadInfoNotifs = this.newNotifs.filter((el) =>
                this.isOnRoadInfoNotification(el)
              );
              const otherNotifs = this.newNotifs.filter(
                (el) =>
                  !this.isNewOrderNotification(el) &&
                  !this.isOnRoadInfoNotification(el)
              );

              if (newOrderNotifs.length) {
                this.playNewOrderAlert();
                newOrderNotifs.forEach((el) => {
                  const requestId = el.reqId || el.requestId || el.orderId;
                  if (el?.id) {
                    this.openedNewOrderNotifIds.add(el.id);
                  }
                  if (requestId) {
                    this.newOrderDialog.open({
                      requestId,
                      notificationId: el.id,
                    });
                  } else {
                    this.messageService.add({
                      severity: 'success',
                      summary: 'New order',
                      detail: el.message || 'You have a new order',
                    });
                  }
                });
              }

              if (onRoadInfoNotifs.length) {
                onRoadInfoNotifs.forEach((el) => {
                  if (el?.id) {
                    this.openedNewOrderNotifIds.add(el.id);
                  }
                });
                const latestOnRoad = onRoadInfoNotifs[0];
                this.messageService.add({
                  severity: 'info',
                  summary: latestOnRoad?.title || 'On-road order',
                  detail:
                    latestOnRoad?.message ||
                    'One of your providers received an on-road order.',
                  life: 8000,
                });
                if (!newOrderNotifs.length) {
                  this.playNotificationAlert();
                }
              }

              if (otherNotifs.length) {
                otherNotifs.forEach((el) => {
                  if (el?.id) {
                    this.openedNewOrderNotifIds.add(el.id);
                  }
                });
                const latestOther = otherNotifs[0];
                this.messageService.add({
                  severity: 'info',
                  summary: latestOther?.title || 'Notification',
                  detail: latestOther?.message || 'You have a new notification',
                });
                if (!newOrderNotifs.length && !onRoadInfoNotifs.length) {
                  this.playNotificationAlert();
                }
              }

              setTimeout(() => {
                this.newNotifs.forEach((el) => {
                  if (!el?.id) {
                    return;
                  }
                  this.afs
                    .collection('company-notifications')
                    .doc(el.id)
                    .update({
                      shown: true,
                    })
                    .catch((err) => console.log(err));
                });
              }, 1000);
            }
          });
      }
    });
  }

  getCancellationWarnings() {
    this.newCancellationWarnings = [];
    this.afAuth.authState.subscribe((user) => {
      if (!user) {
        return;
      }

      this.afs
        .collection('users')
        .doc(user.uid)
        .valueChanges()
        .subscribe((profile: any) => {
          if (profile?.role !== 'company') {
            this.cancellationWarnings = [];
            this.newCancellationWarnings = [];
            return;
          }

          this.afs
            .collection('cancellationWarning', (ref) =>
              ref.where('receiverId', '==', user.uid).where('seen', '==', false)
            )
            .valueChanges({ idField: 'id' })
            .subscribe((res) => {
              this.cancellationWarnings = res;
              this.newCancellationWarnings = this.cancellationWarnings.filter(
                (el) => !el.shown
              );

              if (this.newCancellationWarnings.length !== 0) {
                const latest = this.newCancellationWarnings[0];
                const isServiceDisabled =
                  String(latest?.type || '') === 'providerServiceDisabled';
                this.messageService.add({
                  severity: isServiceDisabled ? 'info' : 'warn',
                  summary: isServiceDisabled
                    ? 'Service disabled'
                    : 'Provider Cancellation',
                  detail:
                    latest?.message ||
                    (isServiceDisabled
                      ? 'One of your providers disabled a service.'
                      : 'One of your providers cancelled an assigned order.'),
                  life: 8000,
                });

                setTimeout(() => {
                  this.newCancellationWarnings.forEach((el) => {
                    this.afs
                      .collection('cancellationWarning')
                      .doc(el.id)
                      .update({
                        shown: true,
                      })
                      .then(() => {
                        console.log('Cancellation warning shown to the manager');
                      });
                  });
                }, 1000);

                this.playCancellationAlert();
              }
            });
        });
    });
  }
}
